import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LuCalendar, LuClock, LuArrowRight } from 'react-icons/lu';
import { blogs } from '../data/blogs';
import { useState } from 'react';

export default function Blog() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const categories = ['All', ...new Set(blogs.map(blog => blog.category))];
  
  const filteredBlogs = selectedCategory === 'All' 
    ? blogs 
    : blogs.filter(blog => blog.category === selectedCategory);

  return (
    <div className="bg-slate-950 pt-32" data-testid="blog-page">
      <section className="py-24 md:py-32" data-testid="blog-hero">
        <div className="container mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center space-y-6">
            <span className="inline-block bg-gradient-to-r from-blue-500 to-orange-500 bg-clip-text text-transparent text-sm font-semibold uppercase tracking-widest">Our Blog</span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight text-white leading-tight">
              Insights & <span className="text-gradient-orange">Best Practices</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
              Engineering insights, technical articles, and industry best practices from our team.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-3 mt-12"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-lg shadow-orange-500/30'
                    : 'bg-slate-900/50 text-slate-300 border border-slate-800 hover:border-orange-500/30'
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="pb-24" data-testid="blog-posts">
        <div className="container mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.6 }}
                whileHover={{ y: -8 }}
                className="group bg-slate-900/50 border border-slate-800 rounded-3xl overflow-hidden hover:border-blue-500/40 transition-all duration-300"
                data-testid={`blog-post-${index}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className={`px-3 py-1 rounded-full border ${
                      index % 2 === 0 
                        ? 'bg-blue-600/10 border-blue-500/20 text-blue-400'
                        : 'bg-orange-600/10 border-orange-500/20 text-orange-400'
                    }`}>
                      {post.category}
                    </span>
                    <div className="flex items-center space-x-3 text-slate-500">
                      <span className="flex items-center space-x-1">
                        <LuCalendar size={14} />
                        <span>{post.date}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <LuClock size={14} />
                        <span>{post.readTime}</span>
                      </span>
                    </div>
                  </div>
                  
                  <h2 className="font-heading text-xl font-bold text-white group-hover:text-orange-400 transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                  
                  <p className="text-slate-400 text-sm leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center space-x-2 text-orange-400 text-sm font-medium group-hover:space-x-3 transition-all"
                  >
                    <span>Read More</span>
                    <LuArrowRight size={16} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}